/**
 * IndexStaffingTableRows.tsx
 * Index tests inside "① Tests on this job" (2026-07-28 — owner request: the
 * pooled index-test allocations were exiled to a bottom section; they now live
 * in the SAME table as the test rows, under an "Index tests" divider band, with
 * the exact column grid and chip/add affordances the test rows use).
 *
 * Rendered as an extra `<TableBody>` inside TaskStaffingTable's `<Table>`
 * (via its `extraRows` slot) so the Test / Dates / Tests / Assigned crew
 * columns align pixel-for-pixel.
 *
 * Behavior parity with the test rows:
 *   - "+ add" opens the SAME AssignPickerPopover (qualified-first ordering).
 *     Assign = index endpoint (advisory skill-gate bypassed, the nudge below
 *     handles mentorship) + the count-once %-fill for the allocation's window
 *     weeks (model.fillSourceShareForWindow) so Team capacity sees the person
 *     immediately — never present-but-0%.
 *   - "Needs mentor" nudge chip when a junior is on the allocation without an
 *     L3+ — opens the picker filtered to mentors.
 *   - Chip × removes with the same ConfirmDialog copy.
 *
 * Team-capacity visibility is the BE half (sourceRosterService reads index
 * presence) — this component only has to keep the % row filled on assign.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, TableBody, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ScienceIcon from '@mui/icons-material/Science';
import EngineeringRoundedIcon from '@mui/icons-material/EngineeringRounded';
import SchoolIcon from '@mui/icons-material/School';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, useTheme } from '@mui/material/styles';

import { InitialsAvatar } from '../../../../components/schedule/InitialsAvatar';
import { SkillBadge } from './SkillBadge';
import { AssignPickerPopover } from './AssignPickerPopover';
import { weekSpanLabel } from './TaskStaffingTable';
import { staffHair, STAFF_CHIP_RADIUS } from './staffViewStyle';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../../components/shared/ConfirmDialog';
import { taskNeedsMentor } from '../../../../utils/mentorNudge';
import { MENTOR_SKILL_LEVEL } from '../../../../utils/rosterAllocation';
import { isQualifiedForTaskType } from '../../../../utils/scheduleQualification';
import { sortCandidatesForTask, type StaffCandidate } from './staffSkillModel';
import {
  assignToIndexAllocation,
  listIndexAllocationAssignees,
  unassignFromIndexAllocation,
  type IndexAllocation,
  type IndexAllocationAssignee,
} from '../../../../services/scheduleIndex';
import { listAssignableUsers, type AssignableUser } from '../../../../services/scheduleAssignments';
import type { UseStaffJobModelResult } from './useStaffJobModel';

export interface IndexStaffingTableRowsProps {
  allocations: IndexAllocation[];
  model: UseStaffJobModelResult;
  locked: boolean;
  photoByEmail?: Map<string, string | null>;
  /** Fires after any successful assign/unassign (parent emits scheduleBus + capacity refresh). */
  onChanged: () => void;
  /**
   * Bump to re-fetch every row's assignee chips (BUG-2026-0174): a Team-capacity
   * roster add now staffs the person on the index allocations server-side, so
   * the chips must reload on the same capacity-refresh signal — they used to
   * load once on mount and missed the add until F5.
   */
  refreshKey?: number;
}

/** One allocation's staffing row — its own hooks (assignees + candidates + writes). */
const IndexAllocationTableRow: React.FC<{
  allocation: IndexAllocation;
  model: UseStaffJobModelResult;
  locked: boolean;
  photoByEmail?: Map<string, string | null>;
  onChanged: () => void;
  refreshKey?: number;
}> = ({ allocation, model, locked, photoByEmail, onChanged, refreshKey }) => {
  const theme = useTheme();
  const taskTypeKey = allocation.TaskTypeKey;

  const [assignees, setAssignees] = useState<IndexAllocationAssignee[]>([]);
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState<{ anchorEl: HTMLElement; mentorsOnly: boolean } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const closeConfirm = () => setConfirm((c) => ({ ...c, open: false }));

  const loadAssignees = useCallback(async () => {
    try {
      setAssignees(await listIndexAllocationAssignees(allocation.IndexAllocationKey));
    } catch {
      // Best-effort — chips stay at whatever was last loaded.
    }
  }, [allocation.IndexAllocationKey]);

  // refreshKey (BUG-2026-0174): re-fetch when the parent's capacity-refresh
  // signal bumps — a roster add in Team capacity staffs people on the index
  // allocations, and the chips must pick that up without a remount.
  useEffect(() => {
    void loadAssignees();
  }, [loadAssignees, refreshKey]);

  // Eager assignable-user fetch (small list, one call per allocation) — powers
  // BOTH the chips' skill badges and the picker candidates.
  useEffect(() => {
    let cancelled = false;
    listAssignableUsers(taskTypeKey)
      .then((list) => { if (!cancelled) setUsers(list); })
      .catch(() => { /* best-effort */ });
    return () => { cancelled = true; };
  }, [taskTypeKey]);

  const levelByUser = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const u of users) m.set(u.AppUserKey.toLowerCase(), u.SkillLevel ?? null);
    return m;
  }, [users]);
  const skillRequired = useMemo(() => users.some((u) => u.SkillLevel != null), [users]);

  const people = useMemo(() => {
    const seen = new Map<string, { userKey: string; displayName: string; photoUrl: string | null; level: number | null }>();
    for (const a of assignees) {
      const lk = a.UserKey.toLowerCase();
      if (!seen.has(lk)) {
        seen.set(lk, {
          userKey: a.UserKey,
          displayName: a.displayName,
          photoUrl: (a.email ? photoByEmail?.get(a.email) : null) ?? a.photoUrl ?? null,
          level: levelByUser.get(lk) ?? null,
        });
      }
    }
    return [...seen.values()].sort((x, y) => x.displayName.localeCompare(y.displayName));
  }, [assignees, levelByUser, photoByEmail]);

  const assignedKeys = useMemo(() => new Set(people.map((p) => p.userKey.toLowerCase())), [people]);

  const candidates: StaffCandidate[] = useMemo(() => {
    const base = users
      .filter((u) => !assignedKeys.has(u.AppUserKey.toLowerCase()) && isQualifiedForTaskType(u, taskTypeKey))
      .map((u) => ({
        userKey: u.AppUserKey,
        displayName: u.DisplayName,
        level: u.SkillLevel ?? null,
        allocationPercent: model.allocPeakByUser.get(u.AppUserKey.toLowerCase()) ?? 0,
      }));
    const filtered = picker?.mentorsOnly
      ? base.filter((c) => typeof c.level === 'number' && c.level >= MENTOR_SKILL_LEVEL)
      : base;
    return sortCandidatesForTask(filtered, skillRequired);
  }, [users, assignedKeys, taskTypeKey, model.allocPeakByUser, picker?.mentorsOnly, skillRequired]);

  const assign = useCallback(
    (userKey: string) => {
      setSaving(true);
      void (async () => {
        try {
          // Advisory gate bypassed (parity with the test rows — the "Needs
          // mentor" nudge below is the mentoring surface, not a modal).
          await assignToIndexAllocation(allocation.IndexAllocationKey, userKey, true);
          // Count-once %-fill for the allocation's weeks (Team capacity).
          await model.fillSourceShareForWindow(userKey, allocation.StartDate, allocation.EndDate);
          await loadAssignees();
          onChanged();
        } catch {
          // Row-level errors surface via the section's snackbar on next change;
          // keep the chip state consistent by reloading.
          await loadAssignees();
        } finally {
          setSaving(false);
        }
      })();
    },
    [allocation, model, loadAssignees, onChanged],
  );

  const unassign = useCallback(
    (userKey: string) => {
      setSaving(true);
      void unassignFromIndexAllocation(allocation.IndexAllocationKey, userKey)
        .then(async () => {
          await loadAssignees();
          // The server also clears the auto-filled weekly share where no other
          // presence remains — refetch so Team capacity drops the person now.
          model.reloadRoster();
          onChanged();
        })
        .catch(async () => loadAssignees())
        .finally(() => setSaving(false));
    },
    [allocation.IndexAllocationKey, loadAssignees, model, onChanged],
  );

  const { weekLabel, rangeLabel } = weekSpanLabel(
    { StartDate: allocation.StartDate, EndDate: allocation.EndDate },
    model.projectWeekColumns,
  );
  const needsMentor = taskNeedsMentor(people.map((p) => p.level), skillRequired);
  const unstaffed = people.length === 0;
  const isEffort = (allocation.Kind ?? 'index') === 'effort';

  return (
    <>
      <TableRow
        sx={{
          '&:hover td': { backgroundColor: theme.schedule.selectionBg },
          '& td': { borderBottom: `1px solid ${staffHair(theme)}` },
        }}
      >
        <TableCell sx={{ py: 1.2, verticalAlign: 'middle' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
              {allocation.TaskTypeName ?? 'Index test'}
            </Typography>
            <Tooltip
              title={isEffort
                ? 'People-effort line (no machine) — staffing covers the whole allocation window; tune each week’s share in Team capacity below.'
                : 'Pooled index test — runs on the shared Index machine; staffing covers the whole allocation window.'}
              arrow
            >
              <Box component="span" sx={{ fontSize: '0.6rem', fontWeight: 700, color: theme.schedule.machineTime, backgroundColor: alpha(theme.schedule.machineTime, 0.12), borderRadius: '999px', px: 0.7, py: 0.15 }}>
                {isEffort ? 'effort' : 'index'}
              </Box>
            </Tooltip>
          </Box>
        </TableCell>
        <TableCell sx={{ py: 1.2, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
          {weekLabel ? (
            <Box
              component="span"
              sx={{
                fontSize: '0.68rem', fontWeight: 700, color: theme.schedule.committed,
                backgroundColor: theme.schedule.selectionBg, border: `1px solid ${staffHair(theme)}`,
                borderRadius: `${STAFF_CHIP_RADIUS}px`, px: 0.75, py: 0.2, display: 'inline-flex', alignItems: 'baseline', gap: 0.5,
              }}
            >
              {weekLabel}
              {rangeLabel && <Box component="span" sx={{ fontSize: '0.6rem', fontWeight: 500, color: 'text.secondary' }}>{rangeLabel}</Box>}
            </Box>
          ) : (
            <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>{rangeLabel ?? '—'}</Typography>
          )}
        </TableCell>
        <TableCell sx={{ py: 1.2, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
          {isEffort ? (
            <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>—</Typography>
          ) : (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.4 }}>
              <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 800, color: theme.schedule.machineTime, lineHeight: 1 }}>
                {allocation.TestCount}
              </Typography>
              <Typography component="span" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                {allocation.TestCount === 1 ? 'test' : 'tests'}
              </Typography>
            </Box>
          )}
        </TableCell>
        <TableCell sx={{ py: 1.05 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
            {unstaffed && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: theme.schedule.maintenance }}>
                <WarningAmberRoundedIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700 }}>Unstaffed</Typography>
              </Box>
            )}
            {people.map((p) => (
              <Box
                key={p.userKey}
                sx={{
                  position: 'relative', width: 142, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.6,
                  border: `1px solid ${staffHair(theme)}`, borderRadius: '999px',
                  pl: 0.35, pr: 0.7, py: 0.3, backgroundColor: theme.palette.background.paper,
                  '&:hover .rmx': { opacity: 1 },
                }}
              >
                <InitialsAvatar displayName={p.displayName} seed={p.userKey} photoUrl={p.photoUrl} size={20} />
                <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.72rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.displayName.split(' ')[0]}
                </Typography>
                <SkillBadge level={p.level} skillRequired={skillRequired} variant="compact" size="sm" />
                {!locked && (
                  <IconButton
                    className="rmx"
                    aria-label={`Remove ${p.displayName.split(' ')[0]} from this index test`}
                    onClick={() => setConfirm({
                      open: true,
                      title: 'Remove from this index test?',
                      message: `Remove ${p.displayName.split(' ')[0]} from ${allocation.TaskTypeName ?? 'this index test'}? They stay on the job's other work.`,
                      confirmLabel: 'Remove',
                      confirmColor: 'error',
                      onConfirm: () => unassign(p.userKey),
                    })}
                    sx={{
                      position: 'absolute', top: -7, right: -7, p: 0.15, opacity: 0, transition: 'opacity .12s',
                      backgroundColor: theme.palette.background.paper, border: `1px solid ${staffHair(theme)}`, color: 'text.secondary',
                      '&:hover': { backgroundColor: alpha(theme.schedule.heatmapHigh, 0.12), color: theme.schedule.heatmapHigh },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 11 }} />
                  </IconButton>
                )}
              </Box>
            ))}
            {needsMentor && !locked && (
              <Box
                role="button"
                tabIndex={0}
                onClick={(e) => setPicker({ anchorEl: e.currentTarget, mentorsOnly: true })}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setPicker({ anchorEl: e.currentTarget, mentorsOnly: true }); }}
                title="A junior (L1/L2) is on this index test — add an L3+ mentor"
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                  border: `1px solid ${alpha(theme.schedule.maintenance, 0.5)}`,
                  backgroundColor: alpha(theme.schedule.maintenance, 0.1), color: theme.schedule.maintenance,
                  borderRadius: '999px', px: 0.9, py: 0.35,
                  '&:hover': { backgroundColor: alpha(theme.schedule.maintenance, 0.2) },
                }}
              >
                <SchoolIcon sx={{ fontSize: 13 }} />
                <Typography sx={{ fontSize: '0.66rem', fontWeight: 700 }}>Needs mentor</Typography>
              </Box>
            )}
            {!locked && (
              <Tooltip title="Assign someone to this index test" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => setPicker({ anchorEl: e.currentTarget, mentorsOnly: false })}
                  disabled={saving}
                  sx={{
                    color: theme.schedule.employeeAlloc,
                    border: `1px dashed ${alpha(theme.schedule.employeeAlloc, 0.6)}`,
                    borderRadius: '999px', px: 0.7, py: 0.1,
                    '&:hover': { backgroundColor: alpha(theme.schedule.employeeAlloc, 0.1) },
                  }}
                >
                  <PersonAddAlt1RoundedIcon sx={{ fontSize: 14 }} />
                  <Typography component="span" sx={{ fontSize: '0.62rem', fontWeight: 700, ml: 0.3 }}>add</Typography>
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </TableCell>
      </TableRow>

      <AssignPickerPopover
        anchorEl={picker?.anchorEl ?? null}
        candidates={candidates}
        skillRequired={skillRequired}
        onPick={(userKey) => {
          assign(userKey);
          setPicker(null);
        }}
        onClose={() => setPicker(null)}
      />
      <ConfirmDialog state={confirm} onClose={closeConfirm} />
    </>
  );
};

export const IndexStaffingTableRows: React.FC<IndexStaffingTableRowsProps> = ({
  allocations,
  model,
  locked,
  photoByEmail,
  onChanged,
  refreshKey,
}) => {
  const theme = useTheme();
  const indexAllocs = allocations.filter((a) => (a.Kind ?? 'index') === 'index');
  const effortAllocs = allocations.filter((a) => (a.Kind ?? 'index') === 'effort');
  if (allocations.length === 0) return null;

  const dividerBand = (icon: React.ReactNode, title: string, blurb: string) => (
    <TableRow>
      <TableCell
        colSpan={4}
        sx={{
          py: 0.7,
          backgroundColor: theme.table.headerBg,
          borderBottom: `1px solid ${staffHair(theme)}`,
          borderTop: `1px solid ${staffHair(theme)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          {icon}
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>
            {blurb}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  const renderRows = (list: IndexAllocation[]) =>
    list.map((a) => (
      <IndexAllocationTableRow
        key={a.IndexAllocationKey}
        allocation={a}
        model={model}
        locked={locked}
        photoByEmail={photoByEmail}
        onChanged={onChanged}
        refreshKey={refreshKey}
      />
    ));

  return (
    <TableBody>
      {indexAllocs.length > 0 && (
        <>
          {dividerBand(
            <ScienceIcon sx={{ fontSize: 14, color: theme.schedule.machineTime }} />,
            'Index tests',
            '— pooled on the shared Index machine; staffing covers the whole window',
          )}
          {renderRows(indexAllocs)}
        </>
      )}
      {effortAllocs.length > 0 && (
        <>
          {dividerBand(
            <EngineeringRoundedIcon sx={{ fontSize: 14, color: theme.schedule.employeeAlloc }} />,
            'Project effort',
            '— people-only lines (no machine); staff them here, tune weekly % in Team capacity',
          )}
          {renderRows(effortAllocs)}
        </>
      )}
    </TableBody>
  );
};
